import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import type { FC } from "react";
import { useMemo } from "react";
import type { Control, FieldValues, Path } from "react-hook-form";
import type { FormSelectRules } from "../../components/formComponents/FormSelect";
import FormSelect from "../../components/formComponents/FormSelect";
import type { Currency } from "../../data/BaseEntity";
import { Price } from "../../misc/price";
import type { InvoiceSellerId, SellerLimits } from "../ordersApi";
import { AUTOMATIC_SELLER_SELECTION } from "./invoiceSellerUtils";
import { useOrderPriceByDepartment } from "./useOrderPriceByDepartment";

function formatAmount(amount: number, currency: Currency): string {
  return new Price(amount).round(2).toDisplayValue(currency);
}

interface SellerLimitsSummaryTableProps {
  readonly availableSellers: InvoiceSellerId[];
  readonly sellerLimitsByKind: Partial<Record<InvoiceSellerId, SellerLimits>>;
  readonly currency: Currency;
}

const SellerLimitsSummaryTable: FC<SellerLimitsSummaryTableProps> = ({
  availableSellers,
  sellerLimitsByKind,
  currency,
}) => (
  <Table size="small" data-testid="seller-limits-summary">
    <TableHead>
      <TableRow>
        <TableCell>Sprzedawca</TableCell>
        <TableCell align="right">Wartość usługi</TableCell>
        <TableCell align="right">Do wystawienia</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {availableSellers.map((sellerId) => {
        const sellerLimits = sellerLimitsByKind[sellerId];
        if (!sellerLimits) {
          return null;
        }
        return (
          <TableRow key={sellerId}>
            <TableCell>{sellerLimits.seller.shortName}</TableCell>
            <TableCell align="right">{formatAmount(sellerLimits.amount, currency)}</TableCell>
            <TableCell align="right">{formatAmount(sellerLimits.availableAmount, currency)}</TableCell>
          </TableRow>
        );
      })}
    </TableBody>
  </Table>
);

interface InvoiceSellerSelectProps<TFieldValues extends FieldValues> {
  readonly orderId: string;
  readonly currency: Currency;
  readonly invoiceKind: "advance" | "proforma";
  readonly control: Control<TFieldValues>;
  readonly name: Path<TFieldValues>;
  readonly rules?: FormSelectRules<TFieldValues>;
}

function InvoiceSellerSelect<TFieldValues extends FieldValues>({
  orderId,
  currency,
  invoiceKind,
  control,
  name,
  rules,
}: InvoiceSellerSelectProps<TFieldValues>) {
  const { data: limits } = useOrderPriceByDepartment(orderId);
  const kindLimits = limits?.[invoiceKind] ?? {};
  const availableSellers = limits?.sellerAssignmentOrder.filter((id) => !!kindLimits[id]) ?? [];

  const sellerSelectOptions = useMemo(
    () => [
      { value: AUTOMATIC_SELLER_SELECTION, label: "Automatyczny" },
      ...availableSellers.map((sellerId) => {
        const sellerLimits = kindLimits[sellerId];
        return {
          value: sellerId,
          label: sellerLimits ? sellerLimits.seller.shortName : sellerId,
        };
      }),
    ],
    [availableSellers, kindLimits],
  );

  if (availableSellers.length === 0) {
    return null;
  }

  return (
    <>
      <FormSelect
        name={name}
        control={control}
        label="Sprzedawca"
        margin="dense"
        options={sellerSelectOptions}
        rules={rules}
      />
      <Box sx={{ mt: 1 }}>
        <SellerLimitsSummaryTable
          availableSellers={availableSellers}
          sellerLimitsByKind={kindLimits}
          currency={currency}
        />
      </Box>
    </>
  );
}

export default InvoiceSellerSelect;
